import { assert } from '@sama/js-core';
import { memoize } from '@sama/micro-dash';
import { DataTexture, FloatType, Matrix4, RGFormat } from 'three';
import { LutData, LutMetadata } from '../../../../team-apis/core/lut-data';
import { Point3D } from '../../../../team-apis/otterdam/answers/shape-typings';
import {
  CalibrationData,
  SingleFrameCalibrationData,
} from '../../../../team-apis/otterdam/calibration-data';
import { H3Material } from '../../../materials/basic/h3-basic-material';
import {
  buildCalibrationMatrix,
  getCameraWorldDirection,
} from '../../calibration-utils';

export function setLutUniforms(
  material: H3Material,
  config: {
    originalCalibrationData: CalibrationData;
    currentFrameCalibrationData: SingleFrameCalibrationData;
    lutData: LutData | undefined;
  },
) {
  assert(config.lutData, 'LUT data is required for LUT projection');

  material.setUniform('lut', buildLutTexture(config.lutData));
  material.setUniform('lutMetadata', config.lutData.metadata);
  material.setUniform(
    'inverseCalibrationMatrix',
    buildCalibrationMatrix(config.originalCalibrationData).invert(),
  );
  material.setUniform(
    'cameraWorldPosition',
    config.currentFrameCalibrationData.position,
  );
  material.setUniform(
    'cameraWorldDirection',
    getCameraWorldDirection(config.currentFrameCalibrationData.rotation),
  );
}

export interface LutUniforms {
  lut: DataTexture;
  lutMetadata: LutMetadata;
  inverseCalibrationMatrix: Matrix4;
  cameraWorldDirection: Point3D;
  cameraWorldPosition: Point3D;
}

export const lutProjection = `
#if defined ( USE_LUT_PROJECTION )

  #define M_PI 3.1415926535897932384626433832795

  precision highp float;
  precision highp int;

  vec4 getLutPosition(vec3 h3Position) {
    vec4 vertexWorldPosition = modelMatrix * vec4( h3Position, 1.0 );
    vec4 cameraTransformed = inverseCalibrationMatrix * vertexWorldPosition;

    float x = cameraTransformed.x;
    float y = cameraTransformed.y;
    float z = cameraTransformed.z;

    // compute alpha/phi
    float phiDeg = atan(y, x) * 180.0 / M_PI;
    float alphaDeg = atan(z, sqrt(x * x + y * y)) * 180.0 / M_PI;

    phiDeg = clamp(phiDeg, lutMetadata.phi_min, lutMetadata.phi_max);
    alphaDeg = clamp(alphaDeg, lutMetadata.alpha_min, lutMetadata.alpha_max);

    // [0..textureSize]
    float tu = (phiDeg - lutMetadata.phi_min) / lutMetadata.phi_step;
    float tv = (alphaDeg - lutMetadata.alpha_min) / lutMetadata.alpha_step;

    // [0..imageSize]
    vec2 uv = texelFetch(lut, ivec2(int(round(tu)), int(round(tv))), 0).rg;

    // [-1..1]
    float clipX = (((uv.x + 1.0) / lutMetadata.image_width) * 2.0) - 1.0;
    float clipY = (((lutMetadata.image_height - (uv.y + 1.0)) / lutMetadata.image_height) * 2.0) - 1.0;

    vec3 cameraToPointDirection = normalize(vertexWorldPosition.xyz - cameraWorldPosition);

    float behindCameraTolerance = -0.1;
    if(dot(cameraWorldDirection, cameraToPointDirection) < behindCameraTolerance){
      return vec4(clipX, clipY, -2.0, 1.0);
    }

    return vec4(clipX, clipY, 1.0, 1.0);
  }

#endif
`;

// We use memoization to avoid unecessary texture updates caused by the creation
// of new objects. LUTs can have sizes greater than 100MB which creates render
// lags if we are uploading a new object to the GPU every time
const buildLutTexture = memoize((lutData: LutData) => {
  const { phi_max, phi_min, phi_step } = lutData.metadata;
  const width = Math.ceil((phi_max - phi_min) / phi_step) + 1;

  const { alpha_max, alpha_min, alpha_step } = lutData.metadata;
  const height = Math.ceil((alpha_max - alpha_min) / alpha_step) + 1;

  const lookUpTexture = new DataTexture(
    lutData.angleToPixel,
    width,
    height,
    RGFormat,
    FloatType,
  );
  lookUpTexture.needsUpdate = true;

  return lookUpTexture;
});
